import {  Component, OnInit, OnDestroy , inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { AuthService } from "../../core/services/auth.service";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { TuiAutoFocus, TuiDay, TuiDayRange } from "@taiga-ui/cdk";
import {
  TuiAlertService,
  TuiButton,
  TuiDialog,
  TuiDropdown,
  TuiError,
  TuiHint,
  TuiLoader,
} from "@taiga-ui/core";
import { TuiTable } from "@taiga-ui/addon-table";
import {
  TuiComboBoxModule,
  TuiInputDateModule,
  TuiInputDateRangeModule,
  TuiInputDateTimeModule,
  TuiInputModule,
  TuiTextareaModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { ShiftService } from "../../core/services/shift.service";
import { DatePipe } from "@angular/common";
import {
  TuiDataListWrapper,
  TuiFieldErrorPipe,
  TuiFilterByInputPipe,
  TuiStringifyContentPipe,
  TuiUnfinishedValidator,
} from "@taiga-ui/kit";
import { catchError, of, Subscription, forkJoin } from "rxjs";
import { ClientService } from "../../core/services/client.service";
import { NurseService } from "../../core/services/nurse.service";
import { LeaveService } from "../../core/services/leave.service";
import { formatClientIdentifier } from "../../shared/utils/client-identifier";
import { EnterSubmitDirective } from "../../core/directives/enter-submit.directive";

@Component({
  selector: "app-nurse-attendence",
  standalone: true,
  imports: [
    EnterSubmitDirective,
    CommonModule,
    ReactiveFormsModule,
    TuiTable,
    TuiButton,
    TuiInputDateRangeModule,
    ReactiveFormsModule,
    FormsModule,
    TuiInputModule,
    TuiDialog,
    TuiLoader,
    TuiHint,
    TuiComboBoxModule,
    TuiTextfieldControllerModule,
    TuiStringifyContentPipe,
    TuiDataListWrapper,
    TuiFilterByInputPipe,
    TuiDropdown,
    TuiFieldErrorPipe,
    TuiError,
    TuiTextareaModule,
    TuiInputDateModule,
  ],
  templateUrl: "./nurse-attendence.component.html",
  styleUrl: "./nurse-attendence.component.scss",
})
export class NurseAttendenceComponent implements OnInit, OnDestroy {
  nurseAttendanceData: any[] = [];
  /** Map key: `${nurseId}|YYYY-MM-DD` -> leave record */
  leaveByNurseDate: Record<string, any> = {};
  loading = false;
  startDate: any;
  endDate: any;
  dateRange: Date[] = [];
  shiftForm: FormGroup;
  clients: any[] = [];
  nurses: any[] = [];
  selectedShiftId: string | null = null;
  loggedInUserCanEdit = false;
  columnWidthPx = 140;

  onColumnWidthChange(width: number): void {
    this.columnWidthPx = width;
  }

  protected readonly searchControl = new FormControl("");

  private getFirstAndLastDayOfCurrentMonth(): TuiDayRange {
    const now = new Date();
    const firstDay = new TuiDay(now.getFullYear(), now.getMonth(), 1);
    const lastDate = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0
    ).getDate();
    const lastDay = new TuiDay(now.getFullYear(), now.getMonth(), lastDate);

    this.startDate = this.formatTuiDay(firstDay);
    this.endDate = this.formatTuiDay(lastDay);

    return new TuiDayRange(firstDay, lastDay);
  }

  protected readonly dateForm = new FormGroup({
    selectedRange: new FormControl(this.getFirstAndLastDayOfCurrentMonth()),
  });

  private subscription: Subscription;
  private authService = inject(AuthService);

  constructor(
    private nurseService: NurseService,
    private leaveService: LeaveService,
    private alerts: TuiAlertService,
    private datePipe: DatePipe,
    private fb: FormBuilder,
    private clientService: ClientService,
    private shiftService: ShiftService
  ) {
    this.subscription = this.dateForm
      .get("selectedRange")!
      .valueChanges.subscribe((value) => {
        if (value) {
          this.startDate = this.formatTuiDay(value.from);
          this.endDate = this.formatTuiDay(value.to);
        }
      });

    this.shiftForm = this.fb.group({
      client: [""],
      nurse: ["", Validators.required],
      address: [{ value: "", disabled: true }, Validators.required],
      mealOption: ["", Validators.required],
      typeOption: ["", Validators.required],
      roster: [null, Validators.required],
      rosterType: ["", Validators.required],
      startDate: [{ value: null, disabled: true }, Validators.required],
      charge: [null, Validators.required],
      salary: [null, Validators.required],
      notes: [""],
    });
  }

  protected open = false;

  get filteredAttendanceData(): any[] {
    const query = (this.searchControl.value ?? "").trim().toLowerCase();
    if (!query) {
      return this.nurseAttendanceData;
    }
    return this.nurseAttendanceData.filter((record) => 
      record.nurseName?.toLowerCase().includes(query) || 
      record.nurserefNo?.toLowerCase().includes(query)
    );
  }

  reset(){

    const range = this.getFirstAndLastDayOfCurrentMonth();
    this.dateForm.get('selectedRange')?.setValue(range);

    this.searchControl.setValue("");

    this.nurseAttendanceData = [];

    this.fetchNurseAttendance();
  }

  protected showDialog(shiftId: string): void {
     if(!this.loggedInUserCanEdit){
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    this.selectedShiftId = shiftId;
    this.loadShiftData(shiftId);
    this.open = true;
  }

  ngOnInit(): void {
    document.documentElement.classList.add('no-page-scroll');
    document.body.classList.add('no-page-scroll');
    this.fetchNurseAttendance();
    this.loadClients();
    this.loadNurse();
    this.checkUserPermission();

    this.shiftForm.get("typeOption")?.valueChanges.subscribe((value: any) => {
      if (value && value.items === "NHP (National Hospital)") {
        this.shiftForm.get("address")?.setValue("NHP (National Hospital)");
        this.shiftForm.get("address")?.disable();
      } else if (
        (value && value.items === "Normal") ||
        value.items === "Baby Care"
      ) {
        this.shiftForm.get("address")?.enable();
      }
    });

    this.shiftForm.get("roster")?.valueChanges.subscribe((value: any) => {
      if (value && value.items === "24 hours shift") {
        const withMealOption = this.mealOptions.find(
          (opt) => opt.items === "With Meal"
        );
        const withRosterType = this.rosterType.find(
          (opt) => opt.items === "Day Shift"
        );
        this.shiftForm.get("mealOption")?.setValue(withMealOption);
        this.shiftForm.get("mealOption")?.disable();
        this.shiftForm.get("rosterType")?.setValue(withRosterType);
        this.shiftForm.get("rosterType")?.disable();
      } else if (value && value.items === "12 hours shift") {
        this.shiftForm.get("mealOption")?.enable();
        this.shiftForm.get("rosterType")?.enable();
      }
    });
  }

  updateShift(): void {
    this.loading = true;
    const shiftPayload = {
      salary: this.shiftForm.get('salary')?.value,
      charges: this.shiftForm.get('charge')?.value,
      rooster: this.shiftForm.get("roster")?.value?.value,
      roosterType: this.shiftForm.get("rosterType")?.value?.items,
      nurseID: this.shiftForm.get('nurse')?.value?._id || this.shiftForm.get('nurse')?.value,
      clientID: this.shiftForm.get('client')?.value?._id || this.shiftForm.get('client')?.value,
      address: this.shiftForm.get('address')?.value,
      mealOption: this.shiftForm.get('mealOption')?.value?.items,
      shiftType: this.shiftForm.get('typeOption')?.value?.items,
      date: this.shiftForm.get('startDate')?.value instanceof TuiDay
        ? this.formatTuiDay(this.shiftForm.get('startDate')?.value)
        : this.shiftForm.get('startDate')?.value,
      notes: this.shiftForm.get('notes')?.value,
    };

    if (this.selectedShiftId) {
      this.shiftService.updateShift(this.selectedShiftId, shiftPayload).subscribe(
        (res: any) => {
          this.alerts
            .open("Shift has been updated successfully.", {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
          this.fetchNurseAttendance();
          this.loading = false;
          this.open = false;
        },
        (error: any) => {
          this.alerts
            .open("Failed to update shift data.", {
              label: "Error",
              appearance: "error",
            })
            .subscribe();
          this.loading = false;
          console.error(error);
        }
      );
    } else {
      console.error("No shift id selected for update.");
      this.loading = false;
    }
  }

  deleteShift(): void {
    if (!this.selectedShiftId) {
      console.error("No shift id selected for deletion.");
      return;
    }

    this.loading = true;
    const shiftPayload = {
      archived: true,
    };

    this.shiftService.updateShift(this.selectedShiftId, shiftPayload).subscribe(
      (res: any) => {
        this.alerts
          .open("Shift has been deleted successfully.", {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
        this.fetchNurseAttendance();
        this.loading = false;
        this.open = false;
      },
      (error: any) => {
        this.alerts
          .open("Failed to delete shift.", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
        this.loading = false;
        console.error(error);
      }
    );
  }

  loadShiftData(shiftId: string): void {
    this.loading = true;
    this.shiftService
      .getShiftById(shiftId)
      .pipe(
        catchError((error) => {
          console.error("Error loading shift data:", error);
          this.loading = false;
          return of(null);
        })
      )
      .subscribe((response: any | null) => {
        if (response && response.data) {
          const nurseId =
            response.data.nurseID?._id || response.data.nurseID || null;
          const clientId =
            response.data.clientID?._id || response.data.clientID || null;
          const startDate = response.data.date
            ? TuiDay.fromLocalNativeDate(new Date(response.data.date))
            : null;

          const patchData = {
            nurse:
              this.nurses?.find((n: any) => n?._id === nurseId) ??
              response.data.nurseID ??
              nurseId,
            client:
              this.clients?.find((c: any) => c?._id === clientId) ??
              response.data.clientID ??
              clientId,
            address: response.data.address,
            mealOption: this.mealOptions.find(
              (option) => option.items === response.data.mealOption
            ),
            typeOption: this.typeOptions.find(
              (option) => option.items === response.data.shiftType
            ),
            roster: this.rosterItems.find(
              (item) => item.value === response.data.rooster
            ),
            rosterType: this.rosterType.find(
              (option) => option.items === response.data.roosterType
            ),
            charge: response.data.charges,
            salary: response.data.salary,
            startDate: startDate,
            notes: response.data.notes,
          };

          this.shiftForm.patchValue(patchData);
          this.rehydrateShiftSelectors();
        }
        this.loading = false;
      });
  }

  private rehydrateShiftSelectors(): void {
    // If the combo-box control holds a raw id string (because lists loaded after patch),
    // replace it with the actual object so the UI can display/track it reliably.
    const nurseVal: any = this.shiftForm.get("nurse")?.value;
    const clientVal: any = this.shiftForm.get("client")?.value;
    if (typeof nurseVal === "string" && this.nurses?.length) {
      const found = this.nurses.find((n: any) => n?._id === nurseVal);
      if (found) this.shiftForm.get("nurse")?.setValue(found, { emitEvent: false });
    }
    if (typeof clientVal === "string" && this.clients?.length) {
      const found = this.clients.find((c: any) => c?._id === clientVal);
      if (found) this.shiftForm.get("client")?.setValue(found, { emitEvent: false });
    }
  }

  loadClients(): void {
    this.clientService.getAllClients().subscribe({
      next: (response: any) => {
        const clients = Array.isArray(response) ? response : [];
        this.clients = clients.filter((c: any) => !c?.archived && c?.active !== false);
        this.rehydrateShiftSelectors();
      },
      error: (error) => {
        console.error("Error fetching clients:", error);
      },
    });
  }

  loadNurse(): void {
    this.nurseService.getAllNurses().subscribe({
      next: (response: any) => {
        const nurses = Array.isArray(response) ? response : [];
        this.nurses = nurses.filter((n: any) => !n?.archived && n?.active !== false);
        this.rehydrateShiftSelectors();
      },
      error: (error) => {
        console.error("Error fetching clients:", error);
      },
    });
  }

  stringifyClients(client: any): string {
    if (!client) return "";
    if (typeof client === "object") {
      return `${formatClientIdentifier(client)} - ${client.name}`;
    }
    const foundClient = this.clients.find((c: any) => c._id === client);
    return foundClient
      ? `${formatClientIdentifier(foundClient)} - ${foundClient.name}`
      : "";
  }

  /** Attendance API exposes `clientReg`; fall back to loaded client for full formatting */
  shiftClientDisplayRef(shift: any): string {
    if (!shift || shift.isOffDay) return "";

    const clientId = shift.clientID;
    if (clientId && this.clients?.length) {
      const found = this.clients.find((c: any) => c._id === clientId);
      if (found) return formatClientIdentifier(found);
    }

    const raw =
      shift.clientReg || shift.registrationNo || shift.refNo || "";
    if (!raw || raw === "N/A") return "";
    return formatClientIdentifier({ refNo: raw });
  }

  stringifyNurses(nurse: any): string {
    if (!nurse) return "";
    if (typeof nurse === "object") {
      return `${nurse.refNo} - ${nurse.name}`;
    }
    const foundNurse = this.nurses.find((c: any) => c._id === nurse);
    return foundNurse
      ? `${foundNurse.refNo} - ${foundNurse.name}`
      : "";
  }

  stringifyDropdown(item: { items: string; value: number } | null): string {
    if (!item) return "";
    return item.items;
  }

  protected readonly rosterItems = [
    { items: "12 hours shift", value: 12 },
    { items: "24 hours shift", value: 24 },
  ];

  protected readonly rosterType = [
    { items: "Day Shift" },
    { items: "Night Shift" },
  ];

  protected readonly mealOptions = [
    { items: "With Meal" },
    { items: "Without Meal" },
  ];

  protected readonly typeOptions = [
    { items: "Normal" },
    { items: "Baby Care" },
    { items: "NHP (National Hospital)" },
  ];

  private formatTuiDay(tuiDay: TuiDay): string {
    return `${tuiDay.year}-${String(tuiDay.month + 1).padStart(
      2,
      "0"
    )}-${String(tuiDay.day).padStart(2, "0")}`;
  }

  private generateDateRange(): void {
    const start = new Date(this.startDate);
    const end = new Date(this.endDate);
    this.dateRange = [];
    while (start <= end) {
      this.dateRange.push(new Date(start));
      start.setDate(start.getDate() + 1);
    }
  }

  getShiftTooltip(shift: any): string {
    if (!shift) return "";
    if (shift.isOffDay) {
      return shift.isLeave
        ? `${shift.leaveType === 'AnnualLeave' ? 'Annual Leave' : 'Day-Off'}\nReason: ${shift.notes || 'On leave'}`
        : `Off Day\nNo work assigned`;
    }
    const lines: string[] = [];
    lines.push(`Client: ${shift.clientName || '—'}`);
    lines.push(`Client ID: ${this.shiftClientDisplayRef(shift)}`);
    lines.push(`Shift: ${shift.shiftType || '—'}`);
    lines.push(`Meal: ${shift.mealOption || '—'}`);
    lines.push(`Roster: ${shift.roster || '—'}`);
    lines.push(`Salary: Rs. ${shift.salary || 0}`);
    if (shift.notes) {
      lines.push(`Notes: ${shift.notes}`);
    }
    return lines.join("\n");
  }

  formatDateToYYYYMMDD(date: Date | string): string {
    if (!date) return '';
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
      return date.substring(0, 10);
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getShiftsForDate(shifts: any[], date: Date, nurseId?: string): any[] {
    const shiftDate = this.formatDateToYYYYMMDD(date);
    const foundShifts = (shifts || []).filter(
      (shift) => shift.date && this.formatDateToYYYYMMDD(shift.date) === shiftDate
    );

    if (foundShifts.length === 0) {
      const leaveKey = nurseId ? `${nurseId}|${shiftDate}` : "";
      const leave = leaveKey ? this.leaveByNurseDate[leaveKey] : null;
      if (leave) {
        const leaveLabel =
          leave.leaveType === "AnnualLeave" ? "Annual Leave" : "Day-Off";
        return [
          {
            _id: `leave-${leave._id}-${shiftDate}`,
            clientName: leaveLabel,
            date: shiftDate,
            isOffDay: true,
            isLeave: true,
            leaveType: leave.leaveType,
            shiftType: leaveLabel,
            mealOption: "N/A",
            roster: leaveLabel,
            salary: 0,
            notes: leave.reason || "On leave",
            refNo: "N/A",
          },
        ];
      }
      return [
        {
          _id: `off-day-${shiftDate}`,
          clientName: "Off Day",
          date: shiftDate,
          isOffDay: true,
          isLeave: false,
          shiftType: "Off Day",
          mealOption: "N/A",
          roster: "Off Day",
          salary: 0,
          notes: "No work assigned",
          refNo: "N/A",
        },
      ];
    }

    return foundShifts;
  }

  hasOffDayForDate(shifts: any[], date: Date, nurseId?: string): boolean {
    const cells = this.getShiftsForDate(shifts, date, nurseId);
    return cells.some((c) => c.isOffDay);
  }

  fetchNurseAttendance(): void {
    this.generateDateRange();
    this.loading = true;
    forkJoin({
      attendance: this.nurseService.getNurseAttendance(
        this.startDate,
        this.endDate
      ),
      leaves: this.leaveService
        .getByDateRange(this.startDate, this.endDate)
        .pipe(catchError(() => of({ data: [] }))),
    }).subscribe({
      next: (res: any) => {
        this.nurseAttendanceData = res.attendance?.data || [];
        this.leaveByNurseDate = {};
        const leaves = res.leaves?.data || [];
        for (const leave of leaves) {
          const nid =
            leave.nurseId?._id?.toString?.() ||
            leave.nurseId?.toString?.() ||
            leave.nurseId;
          for (const d of leave.dates || []) {
            const day = this.formatDateToYYYYMMDD(d);
            this.leaveByNurseDate[`${nid}|${day}`] = leave;
          }
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.alerts
          .open("Failed to fetch nurse attendance data.", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
      },
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case "Ongoing":
        return "status-ongoing";
      case "Upcoming":
        return "status-upcoming";
      case "Completed":
        return "status-completed";
      case "Canceled":
        return "status-canceled";
      case "Absent":
        return "status-absent";
      default:
        return "";
    }
  }
  checkUserPermission(): void {
    this.loggedInUserCanEdit = this.authService.hasAnyPermission(["SHIFT_UPDATE", "SHIFT_DELETE"]);
  }

  ngOnDestroy(): void {
    document.documentElement.classList.remove('no-page-scroll');
    document.body.classList.remove('no-page-scroll');
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
